// AUDIT_LOG_CATEGORIES groups backend AUDIT_LOG_ENTRY_KIND_* values for the
// filter UI. Each category renders as a header in the filter dropdown; under
// it sit one or more coarse-grained groups (e.g. "User activity" / "Admin
// activity" under "Authentication"). A group expands to the underlying kinds
// when the filter request is sent to the backend.
//
// The dot colour is shared by the table row decoration so the two stay
// visually consistent. Keep these in sync when adding new kinds.

export interface AuditLogGroup {
  key: string;
  kinds: string[];
}

export interface AuditLogCategory {
  key: string;
  dot: string;
  groups: AuditLogGroup[];
}

export const AUDIT_LOG_CATEGORIES: AuditLogCategory[] = [
  {
    key: "auth",
    dot: "bg-sky-400",
    groups: [
      {
        key: "userActivity",
        kinds: [
          "userLoggedIn",
          "userLoggedOut",
          "userSessionDeleted",
          "userSessionExpired",
          "userNewSessionIp",
          "userSessionUserAgentChanged",
          "userSessionFingerprintChanged",
        ],
      },
      {
        key: "adminActivity",
        kinds: [
          "adminLoggedIn",
          "adminLoggedOut",
          "adminSessionDeleted",
          "adminSessionExpired",
          "adminNewSessionIp",
          "adminSessionUserAgentChanged",
          "adminSessionFingerprintChanged",
          "autocreateAdmin",
        ],
      },
    ],
  },
  {
    key: "errors",
    dot: "bg-red-500",
    groups: [
      {
        key: "grantErrors",
        kinds: ["grantError", "revokeError", "grantForceCleaned", "grantRevokeAbandoned"],
      },
    ],
  },
  {
    key: "threatIntel",
    dot: "bg-orange-400",
    groups: [
      {
        key: "threatIntelEvents",
        kinds: ["greynoiseBlocked", "greynoiseLookupFailed", "threatIntelStaticBlocked"],
      },
      { key: "threatIntelChanges", kinds: ["createBlockListFeed", "updateBlockListFeed", "deleteBlockListFeed"] },
    ],
  },
  {
    key: "trustEngine",
    dot: "bg-violet-400",
    groups: [
      { key: "trustEngineOutcomes", kinds: ["trustPolicyBlocked", "trustPolicyChallenged", "trustPolicyObserved"] },
      {
        key: "trustEngineChanges",
        kinds: [
          "createTrustPolicy",
          "updateTrustPolicy",
          "deleteTrustPolicy",
          "reorderTrustPolicies",
          "setDefaultTrustPolicy",
          "clearIpIntelCache",
          "createNamedNetwork",
          "updateNamedNetwork",
          "deleteNamedNetwork",
        ],
      },
    ],
  },
  {
    key: "grants",
    dot: "bg-emerald-400",
    groups: [
      { key: "userGrants", kinds: ["clickedToGrant", "clickedToRevoke", "grantExpired"] },
      {
        key: "adminGrants",
        kinds: [
          "adminManualGrant",
          "adminOverrideGrant",
          "adminRevokeAgenticAccess",
          "adminRevokeDelegated",
          "adminRevokeGrant",
        ],
      },
      { key: "grantRefusals", kinds: ["grantIpExcluded", "discoveredIpFiltered"] },
    ],
  },
  // The "acl" kinds record a knoc, and the "knoc" kinds an integration. The
  // stored kind names predate the rename and stay; only the labels follow it.
  {
    key: "acls",
    dot: "bg-violet-400",
    groups: [{ key: "aclChanges", kinds: ["createAcl", "updateAcl", "saveKnoc", "deleteAcl"] }],
  },
  {
    key: "groups",
    dot: "bg-violet-400",
    groups: [{ key: "groupChanges", kinds: ["createGroup", "updateGroup", "deleteGroup"] }],
  },
  {
    key: "users",
    dot: "bg-indigo-400",
    groups: [
      {
        key: "userChanges",
        kinds: [
          "createUser",
          "resetUserOtp",
          "updateUser",
          "userChangedPassword",
          "deleteUser",
          "forgetKnownBrowser",
          "deleteAppLogBundle",
        ],
      },
    ],
  },
  {
    key: "admins",
    dot: "bg-indigo-400",
    groups: [
      {
        key: "adminChanges",
        kinds: ["createAdmin", "resetAdminOtp", "updateAdmin", "deleteAdmin", "updateAdminLoginSources"],
      },
    ],
  },
  {
    key: "agents",
    dot: "bg-amber-400",
    groups: [
      {
        key: "agentChanges",
        kinds: ["createAgent", "updateAgent", "deleteAgent", "agentAdoptedHostname", "updateRefusedAgent"],
      },
      // An integration check is filed under agents rather than knocs: it
      // changes nothing, and what it does is make an agent talk to a device.
      { key: "integrationChecks", kinds: ["validateIntegration", "generateApiKey"] },
    ],
  },
  {
    key: "integrations",
    dot: "bg-pink-400",
    groups: [{ key: "integrationChanges", kinds: ["createKnoc", "updateKnoc", "deleteKnoc"] }],
  },
  {
    key: "apiKeys",
    dot: "bg-cyan-400",
    groups: [
      {
        key: "apiKeyChanges",
        kinds: [
          "createApiKey",
          "updateApiKey",
          "deleteApiKey",
          "createAllowlistSecret",
          "regenerateAllowlistSecret",
          "hashAllowlistSecret",
          "updateAllowlistSecret",
        ],
      },
    ],
  },
  {
    key: "settings",
    dot: "bg-teal-400",
    groups: [
      { key: "settingsChanges", kinds: ["updateSettings"] },
      { key: "fleetChanges", kinds: ["updateServer", "forgetServer"] },
    ],
  },
  { key: "other", dot: "bg-slate-400", groups: [] },
];

// AUDIT_LOG_CATEGORY_KINDS flattens each category to the union of its group
// kinds. Used by call sites that need the per-category kind list without
// caring about the sub-grouping.
export const AUDIT_LOG_CATEGORY_KINDS: Record<string, string[]> = Object.fromEntries(
  AUDIT_LOG_CATEGORIES.map((c) => [c.key, c.groups.flatMap((g) => g.kinds)]),
);

// AUDIT_LOG_GROUPS is the flat list of every group across every category, in
// the same order. The filter dropdown uses this when it needs a simple list.
export const AUDIT_LOG_GROUPS: AuditLogGroup[] = AUDIT_LOG_CATEGORIES.flatMap((c) => c.groups);

export const AUDIT_LOG_GROUP_BY_KIND: Record<string, string> = Object.fromEntries(
  AUDIT_LOG_GROUPS.flatMap((g) => g.kinds.map((k) => [k, g.key] as const)),
);

export const AUDIT_LOG_CATEGORY_BY_KIND: Record<string, string> = Object.fromEntries(
  AUDIT_LOG_CATEGORIES.flatMap((c) => c.groups.flatMap((g) => g.kinds.map((k) => [k, c.key] as const))),
);
